<template>
  <div class="entries-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-inbox"></i>
        Descarga Recebida
      </h1>
      <p class="page-subtitle">Visualize as descargas recebidas das origens selecionadas</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-filter"></i>
          Filtros de Consulta
        </h3>
      </div>
      
      <div class="filters-content">
        <div class="filters-grid">
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-map-location-dot"></i>
              Região *
            </label>
            <div class="multiselect-container">
              <MultiSelect 
                v-model="selectedRegions" 
                :options="availableRegions" 
                placeholder="Selecione as regiões que receberam"
                :showSelectAllButtons="true"
              />
            </div>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-arrow-right"></i>
              Região de Origem (Opcional)
            </label>
            <div class="multiselect-container">
              <MultiSelect 
                v-model="selectedOriginRegions" 
                :options="availableOriginRegions" 
                placeholder="Selecione as regiões que enviaram (deixe vazio para todas)"
                :showSelectAllButtons="true"
              />
            </div>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Início *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="startDate"
              placeholder="Data de início"
            />
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Fim *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="endDate"
              placeholder="Data de fim"
            />
          </div>
        </div>

        <div class="filters-actions">
          <button 
            type="button" 
            class="btn-primary" 
            @click="searchDescargas()"
            :disabled="loading"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-search"></i>
            {{ loading ? 'Consultando...' : 'Pesquisar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados...</span>
      </div>
    </div>

    <!-- Resumo Consolidado -->
    <div v-show="!loading && searchExecuted && descargaData" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-chart-bar"></i>
          Resumo Consolidado
        </h3>
      </div>
      
      <div class="summary-content">
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-value currency-positive">{{ setCurrencyString(descargaData?.bet || 0) }}</div>
            <div class="summary-label">Total Vendas</div>
          </div>
          <div class="summary-item">
            <div class="summary-value currency-negative">{{ setCurrencyString(descargaData?.commission || 0) }}</div>
            <div class="summary-label">Total Comissões</div>
          </div>
          <div class="summary-item">
            <div class="summary-value" :class="{ 'currency-positive': (descargaData?.net || 0) > 0, 'currency-negative': (descargaData?.net || 0) < 0 }">{{ setCurrencyString(descargaData?.net || 0) }}</div>
            <div class="summary-label">Líquido Total</div>
          </div>
          <div class="summary-item">
            <div class="summary-value currency-negative">{{ setCurrencyString(descargaData?.award || 0) }}</div>
            <div class="summary-label">Total Prêmios</div>
          </div>
          <div class="summary-item" v-if="shouldShowLiquido2AndRetorno">
            <div class="summary-value" :class="{ 'currency-positive': calculateTotalLiquidoTwo() > 0, 'currency-negative': calculateTotalLiquidoTwo() < 0 }">{{ setCurrencyString(calculateTotalLiquidoTwo()) }}</div>
            <div class="summary-label">Líquido 2 Total</div>
          </div>
          <div class="summary-item">
            <div class="summary-value final-balance" :class="{ 'currency-positive': (descargaData?.total || 0) > 0, 'currency-negative': (descargaData?.total || 0) < 0 }">{{ setCurrencyString(descargaData?.total || 0) }}</div>
            <div class="summary-label">Total Final</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabela de Descargas por Região -->
    <div v-show="!loading && searchExecuted && descargaData?.descargas?.length > 0" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-table"></i>
          Detalhes por Região
        </h3>
        <div class="card-actions">
          <div class="table-zoom-controls" title="Zoom da tabela">
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom <= 5"
              @click="decreaseTableZoom"
              title="Diminuir zoom"
            >
              <i class="fa-solid fa-magnifying-glass-minus"></i>
            </button>
            <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
              {{ tableZoom }}%
            </span>
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom >= 100"
              @click="increaseTableZoom"
              title="Aumentar zoom"
            >
              <i class="fa-solid fa-magnifying-glass-plus"></i>
            </button>
          </div>
          <div class="columns-dropdown" ref="columnsDropdown">
            <button
              type="button"
              class="btn-action btn-columns"
              @click.stop="showColumnsMenu = !showColumnsMenu"
              title="Mostrar ou esconder colunas"
            >
              <i class="fa-solid fa-columns"></i>
              Colunas
              <i class="fa-solid fa-caret-down columns-caret"></i>
            </button>
            <div v-if="showColumnsMenu" class="columns-menu" @click.stop>
              <div class="columns-menu-header">
                <span>Colunas visíveis</span>
                <button type="button" class="columns-reset" @click="resetVisibleColumns">
                  Restaurar
                </button>
              </div>
              <label
                v-for="col in toggleableColumns"
                :key="col.key"
                class="columns-menu-item"
                :class="{ locked: col.locked }"
              >
                <input
                  type="checkbox"
                  :checked="isColumnVisible(col.key)"
                  :disabled="col.locked"
                  @change="toggleColumn(col.key)"
                />
                <span>{{ col.label }}</span>
              </label>
            </div>
          </div>
          <button 
            type="button" 
            class="btn-action btn-print" 
            @click="printReport()"
            title="Imprimir relatório"
          >
            <i class="fa-solid fa-print"></i>
            Imprimir
          </button>
          <button 
            type="button" 
            class="btn-action btn-pdf" 
            @click="exportToPDF()"
            title="Exportar para PDF"
          >
            <i class="fa-solid fa-file-pdf"></i>
            PDF
          </button>
          <button 
            type="button" 
            class="btn-action btn-excel" 
            @click="exportToExcel()"
            title="Exportar para Excel"
          >
            <i class="fa-solid fa-file-excel"></i>
            Excel
          </button>
        </div>
      </div>
      
      <div class="table-container" :style="tableZoomStyle">
        <table class="modern-table">
          <thead>
            <tr>
              <th v-if="isColumnVisible('region')">Região</th>
              <th v-if="isColumnVisible('saldoAnterior')">Saldo Anterior</th>
              <th v-if="isColumnVisible('bet')">Vendas(+)</th>
              <th v-if="isColumnVisible('commission')">Comissão(-)</th>
              <th v-if="isColumnVisible('net')">Líquido</th>
              <th v-if="isColumnVisible('award')">Prêmios(-)</th>
              <th v-if="shouldShowLiquido2AndRetorno && isColumnVisible('liquidoTwo')">Líquido 2</th>
              <th v-if="shouldShowLiquido2AndRetorno && isColumnVisible('returnValue')">Valor Retorno</th>
              <th v-if="isColumnVisible('lpDescarga')">Descarga L.P</th>
              <th v-if="isColumnVisible('bancaReceived')">Banca Recebeu(+)</th>
              <th v-if="isColumnVisible('bancaPay')">Banca Pagou(-)</th>
              <th v-if="isColumnVisible('total')">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="descarga in (descargaData?.descargas || [])" 
              :key="descarga.regionId"
              class="data-row"
            >
              <td v-if="isColumnVisible('region')">
                <div class="region-cell">
                  <i class="fa-solid fa-map-marker-alt"></i>
                  <span>{{ descarga.regionName }}</span>
                </div>
              </td>
              <td v-if="isColumnVisible('saldoAnterior')">
                <span :class="['currency-value', { 'currency-positive': (descarga.saldoAnterior || 0) > 0, 'currency-negative': (descarga.saldoAnterior || 0) < 0 }]">{{ setCurrencyString(descarga.saldoAnterior || 0) }}</span>
              </td>
              <td v-if="isColumnVisible('bet')">
                <span class="currency-value currency-positive">{{ setCurrencyString(descarga.bet) }}</span>
              </td>
              <td v-if="isColumnVisible('commission')">
                <span class="currency-value currency-negative">{{ setCurrencyString(descarga.commission) }}</span>
              </td>
              <td v-if="isColumnVisible('net')">
                <span :class="['currency-value', { 'currency-positive': (descarga.net || 0) > 0, 'currency-negative': (descarga.net || 0) < 0 }]">{{ setCurrencyString(descarga.net || 0) }}</span>
              </td>
              <td v-if="isColumnVisible('award')">
                <span class="currency-value currency-negative">{{ setCurrencyString(descarga.award || 0) }}</span>
              </td>
              <td v-if="shouldShowLiquido2AndRetorno && isColumnVisible('liquidoTwo')">
                <span :class="['currency-value', { 'currency-positive': (descarga.liquidoTwo || 0) > 0, 'currency-negative': (descarga.liquidoTwo || 0) < 0 }]">{{ setCurrencyString(descarga.liquidoTwo || 0) }}</span>
              </td>
              <td v-if="shouldShowLiquido2AndRetorno && isColumnVisible('returnValue')">
                <span class="currency-value">{{ setCurrencyString(descarga.returnValue || 0) }}</span>
              </td>
              <td v-if="isColumnVisible('lpDescarga')">
                <span :class="['currency-value', { 'currency-positive': (descarga.lpDescarga || 0) > 0, 'currency-negative': (descarga.lpDescarga || 0) < 0 }]">{{ setCurrencyString(descarga.lpDescarga || 0) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaReceived')">
                <span class="currency-value currency-positive">{{ setCurrencyString(descarga.bancaReceived || 0) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaPay')">
                <span class="currency-value currency-negative">{{ setCurrencyString(descarga.bancaPay || 0) }}</span>
              </td>
              <td v-if="isColumnVisible('total')">
                <span :class="['currency-value', 'final-balance', { 'currency-negative': (descarga.total || 0) < 0, 'currency-positive': (descarga.total || 0) > 0 }]">
                  {{ setCurrencyString(descarga.total || 0) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Estado vazio -->
    <div v-show="!loading && searchExecuted && (!descargaData?.descargas?.length)" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-inbox"></i>
        <h3>Nenhum dado encontrado</h3>
        <p>Selecione pelo menos uma região e o período para consultar as descargas recebidas</p>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import MultiSelect from "@/components/utils/MultiSelect.vue";
import { ROLES } from "@/services/permissions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const DESCARGAS_RECEBIDA_COLUMNS_STORAGE_KEY = "descargasRecebida.visibleColumns";
const TABLE_ZOOM_STORAGE_KEY = "ab.resultsTableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

const DEFAULT_VISIBLE_COLUMNS = {
  region: true,
  saldoAnterior: true,
  bet: true,
  commission: true,
  net: true,
  award: true,
  liquidoTwo: true,
  returnValue: true,
  lpDescarga: true,
  bancaReceived: true,
  bancaPay: true,
  total: true,
};

export default {
  name: "ViewDescargasRecebida",
  components: { MultiSelect },
  data() {
    return {
      loading: true,
      searchExecuted: false,
      regions: [],
      descargaData: { descargas: [] },
      selectedRegions: [],
      selectedOriginRegions: [],
      startDate: "",
      endDate: "",
      currentRegionId: "",
      
      // Controle de usuário e permissões
      currentUser: null,
      userResponsibleRegions: [],
      userBancaId: null,
      bancas: [],

      // Controle de colunas da tabela
      showColumnsMenu: false,
      visibleColumns: { ...DEFAULT_VISIBLE_COLUMNS },
      tableZoom: 100,
    };
  },
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },

    currentSelectedRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },

    currentSelectedBanca() {
      const savedBanca = localStorage.getItem('selectedBanca');
      return savedBanca ? JSON.parse(savedBanca) : null;
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    availableRegions() {
      let regions = this.regions;
      
      if (this.isAdmin) {
        // ADMIN: aplicar filtro por banca selecionada primeiro se houver
        if (this.currentSelectedBanca) {
          regions = regions.filter(region => 
            String(region.bancaId) === String(this.currentSelectedBanca.id)
          );
        }
        
        // Para ADMIN, aplicar filtro por região ativa selecionada se houver
        if (this.currentSelectedRegion) {
          // Buscar a região selecionada e suas vinculadas
          const selectedRegionId = String(this.currentSelectedRegion.id);
          const selectedRegion = this.regions.find(r => String(r.id) === selectedRegionId);
          
          regions = regions.filter(region => {
            const regionId = String(region.id);
            
            // SEMPRE incluir a própria região selecionada
            if (regionId === selectedRegionId) return true;
            
            // Se a região selecionada é de descarga (onlyDescarga = true e tem descargaRegionId)
            // Mostrar apenas ela mesma, não mostrar região normal
            if (selectedRegion && selectedRegion.onlyDescarga && selectedRegion.descargaRegionId) {
              // Apenas a própria região de descarga
              return false;
            }
            
            // Se a região selecionada é redescarga (onlyDescarga = true e não tem descargaRegionId)
            // Mostrar apenas ela mesma
            if (selectedRegion && selectedRegion.onlyDescarga && !selectedRegion.descargaRegionId) {
              return false;
            }
            
            // CASO 1: Região selecionada ENVIA descarga (tem DescargaRegionId)
            if (selectedRegion && selectedRegion.descargaRegionId) {
              // Incluir a região de destino da descarga
              if (String(selectedRegion.descargaRegionId) === regionId) return true;
            }
            
            // CASO 2: Região selecionada RECEBE descarga (DescargaRegionId = null ou é destino)
            if (!selectedRegion?.descargaRegionId) {
              // Se a região selecionada não tem DescargaRegionId (é região de destino)
              // Incluir todas as regiões que têm a região selecionada como destino (ORIGENS)
              if (region.descargaRegionId && String(region.descargaRegionId) === selectedRegionId) return true;
            } else {
              // Se a região selecionada tem DescargaRegionId, também pode receber de outras
              // Incluir regiões que enviam para a região selecionada (ORIGENS)
              if (region.descargaRegionId && String(region.descargaRegionId) === selectedRegionId) return true;
            }
            
            return false;
          });
        }
        
        // Para ADMIN, mostrar todas as regiões (filtradas por banca/região se selecionada)
        return regions.map(region => {
          return {
            ...region,
            label: region.name,
            value: region.id
          };
        });
      } else {
        // Para usuários não-admin, aplicar filtro por região ativa selecionada se houver
        if (this.currentSelectedRegion) {
          // Buscar a região selecionada e suas vinculadas
          const selectedRegionId = String(this.currentSelectedRegion.id);
          const selectedRegion = this.regions.find(r => String(r.id) === selectedRegionId);
          
          regions = regions.filter(region => {
            const regionId = String(region.id);

            // SEMPRE incluir a própria região selecionada
            if (regionId === selectedRegionId) return true;

            // Se a região selecionada é de descarga (onlyDescarga = true e tem descargaRegionId)
            // Mostrar apenas ela mesma, não mostrar região normal
            if (selectedRegion && selectedRegion.onlyDescarga && selectedRegion.descargaRegionId) {
              // Apenas a própria região de descarga
              return false;
            }
            
            // Se a região selecionada é redescarga (onlyDescarga = true e não tem descargaRegionId)
            // Mostrar apenas ela mesma
            if (selectedRegion && selectedRegion.onlyDescarga && !selectedRegion.descargaRegionId) {
              return false;
            }

            // CASO 1: Região selecionada ENVIA descarga (tem DescargaRegionId)
            if (selectedRegion && selectedRegion.descargaRegionId) {
              // Incluir a região de destino da descarga
              if (String(selectedRegion.descargaRegionId) === regionId) return true;
            }
            
            // CASO 2: Região selecionada RECEBE descarga (DescargaRegionId = null ou é destino)
            if (!selectedRegion?.descargaRegionId) {
              // Se a região selecionada não tem DescargaRegionId (é região de destino)
              // Incluir todas as regiões que têm a região selecionada como destino (ORIGENS)
              if (region.descargaRegionId && String(region.descargaRegionId) === selectedRegionId) return true;
            } else {
              // Se a região selecionada tem DescargaRegionId, também pode receber de outras
              // Incluir regiões que enviam para a região selecionada (ORIGENS)
              if (region.descargaRegionId && String(region.descargaRegionId) === selectedRegionId) return true;
            }
            
            return false;
          });
        }
        
        // DEPOIS aplicar filtro de permissões: BANCA, GERENTE e COBRADOR: apenas regiões das quais são responsáveis OU vinculadas
        regions = regions.filter(region => 
          this.userResponsibleRegions.includes(region.id) ||
          // OU regiões que enviam descarga para as regiões do usuário
          (region.descargaRegionId && this.userResponsibleRegions.includes(region.descargaRegionId)) ||
          // OU regiões que recebem descarga de uma das regiões do usuário
          this.regions.some(userRegion => 
            this.userResponsibleRegions.includes(userRegion.id) && 
            userRegion.descargaRegionId && 
            String(userRegion.descargaRegionId) === String(region.id)
          )
        );
        
        // Filtrar redescarga: mostrar apenas se estiver logado nela (ou seja, se a região selecionada for redescarga)
        if (this.currentSelectedRegion) {
          const selectedRegion = this.regions.find(r => String(r.id) === String(this.currentSelectedRegion.id));
          const isSelectedRedescarga = selectedRegion && selectedRegion.onlyDescarga && !selectedRegion.descargaRegionId;
          
          // Se não está logado em redescarga, remover todas as regiões redescarga
          if (!isSelectedRedescarga) {
            regions = regions.filter(region => {
              // Remover regiões que são redescarga (onlyDescarga = true e não tem descargaRegionId)
              // Mas manter se for a região selecionada
              if (String(region.id) === String(this.currentSelectedRegion.id)) return true;
              return !(region.onlyDescarga && !region.descargaRegionId);
            });
          }
        } else {
          // Se não há região selecionada, remover todas as redescargas
          regions = regions.filter(region => !(region.onlyDescarga && !region.descargaRegionId));
        }
        
        // Adicionar labels simples com apenas o nome da região
        return regions.map(region => {
          return {
            ...region,
            label: region.name,
            value: region.id
          };
        });
      }
    },

    shouldShowLiquido2AndRetorno() {
      // Mostrar Líquido 2 e Retorno apenas se:
      // 1. O usuário é BANCA, ou
      // 2. Alguma das regiões selecionadas tem descargaRegionId (é região de descarga)
      if (this.isBanca) {
        return true;
      }
      
      // Verificar se há regiões de descarga selecionadas
      if (this.selectedRegions && this.selectedRegions.length > 0) {
        return this.selectedRegions.some(region => {
          const fullRegion = this.regions.find(r => r.id === region.id);
          return fullRegion && fullRegion.descargaRegionId;
        });
      }
      
      return false;
    },

    availableOriginRegions() {
      // Se não há regiões de destino selecionadas, retornar array vazio
      if (!this.selectedRegions || this.selectedRegions.length === 0) {
        return [];
      }

      // Buscar todas as regiões que enviam descarga para as regiões de destino selecionadas
      const destinationIds = this.selectedRegions.map(r => r.id || r);
      const originRegions = this.regions.filter(region => {
        // Região que tem descargaRegionId apontando para uma das regiões de destino selecionadas
        return region.descargaRegionId && destinationIds.includes(region.descargaRegionId);
      });

      // Aplicar filtros de permissão se necessário
      let filteredRegions = originRegions;
      
      if (!this.isAdmin) {
        // Para usuários não-admin, aplicar filtro de permissões
        filteredRegions = originRegions.filter(region => 
          this.userResponsibleRegions.includes(region.id) ||
          // OU regiões que enviam descarga para as regiões do usuário
          (region.descargaRegionId && this.userResponsibleRegions.includes(region.descargaRegionId)) ||
          // OU regiões que recebem descarga de uma das regiões do usuário
          this.regions.some(userRegion => 
            this.userResponsibleRegions.includes(userRegion.id) && 
            userRegion.descargaRegionId && 
            String(userRegion.descargaRegionId) === String(region.id)
          )
        );
      }

      // Retornar no formato do MultiSelect
      return filteredRegions.map(region => {
        const destinationRegion = this.regions.find(r => r.id === region.descargaRegionId);
        return {
          ...region,
          label: destinationRegion 
            ? `${region.name} → ${destinationRegion.name}`
            : region.name,
          value: region.id
        };
      });
    },

    toggleableColumns() {
      const columns = [
        { key: "region", label: "Região", locked: true },
        { key: "saldoAnterior", label: "Saldo Anterior" },
        { key: "bet", label: "Vendas(+)" },
        { key: "commission", label: "Comissão(-)" },
        { key: "net", label: "Líquido" },
        { key: "award", label: "Prêmios(-)" },
      ];

      if (this.shouldShowLiquido2AndRetorno) {
        columns.push(
          { key: "liquidoTwo", label: "Líquido 2" },
          { key: "returnValue", label: "Valor Retorno" },
        );
      }

      columns.push(
        { key: "lpDescarga", label: "Descarga L.P" },
        { key: "bancaReceived", label: "Banca Recebeu(+)" },
        { key: "bancaPay", label: "Banca Pagou(-)" },
        { key: "total", label: "Total" },
      );

      return columns;
    },
  },
  async mounted() {
    this.loadVisibleColumns();
    this.loadTableZoom();
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
    this.loading = true;
    try {
      await this.loadCurrentUser();
      
      // Definir datas padrão (hoje)
      const _d = new Date();
      const _dateStr = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
      this.startDate = _dateStr;
      this.endDate = _dateStr;
      
      await this.searchRegions();
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
  },
  watch: {
    currentSelectedRegion: {
      handler(newRegion, oldRegion) {
        // Quando a região ativa mudar, recarregar as regiões disponíveis
        this.updateSelectedRegions();
      },
      deep: true
    },

    currentSelectedBanca: {
      handler(newBanca, oldBanca) {
        // Quando a banca ativa mudar, recarregar as regiões disponíveis
        this.updateSelectedRegions();
      },
      deep: true
    },

    selectedRegions: {
      handler(newRegions, oldRegions) {
        // Quando as regiões de destino mudarem, limpar as regiões de origem selecionadas
        // para evitar inconsistências (regiões de origem que não enviam para as novas regiões de destino)
        if (newRegions && newRegions.length > 0) {
          // Filtrar apenas as regiões de origem que ainda enviam para as regiões de destino selecionadas
          const destinationIds = newRegions.map(r => r.id || r);
          this.selectedOriginRegions = this.selectedOriginRegions.filter(originRegion => {
            const originId = originRegion.id || originRegion;
            const origin = this.regions.find(r => r.id === originId);
            return origin && origin.descargaRegionId && destinationIds.includes(origin.descargaRegionId);
          });
        } else {
          // Se não há regiões de destino, limpar todas as regiões de origem
          this.selectedOriginRegions = [];
        }
      },
      deep: true
    }
  },
  methods: {
    loadVisibleColumns() {
      try {
        const saved = localStorage.getItem(DESCARGAS_RECEBIDA_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visibleColumns = {
          ...DEFAULT_VISIBLE_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          region: true,
        };
      } catch {
        this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      }
    },

    saveVisibleColumns() {
      localStorage.setItem(
        DESCARGAS_RECEBIDA_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.visibleColumns),
      );
    },

    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },

    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },

    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    isColumnVisible(key) {
      if (key === "region") {
        return true;
      }
      return this.visibleColumns[key] !== false;
    },

    toggleColumn(key) {
      if (key === "region") {
        return;
      }
      this.visibleColumns = {
        ...this.visibleColumns,
        [key]: !this.isColumnVisible(key),
      };
      this.saveVisibleColumns();
    },

    resetVisibleColumns() {
      this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      this.saveVisibleColumns();
    },

    closeColumnsMenu() {
      this.showColumnsMenu = false;
    },

    handleColumnsMenuOutsideClick(event) {
      if (!this.showColumnsMenu) {
        return;
      }
      const dropdown = this.$refs.columnsDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.closeColumnsMenu();
      }
    },

    getVisibleExportColumns() {
      return this.toggleableColumns.filter(col => this.isColumnVisible(col.key));
    },

    buildExportRow(descarga, formatted = false) {
      const currency = (value) =>
        formatted ? this.setCurrencyString(value || 0) : (value || 0);
      const values = {
        region: descarga.regionName || "",
        saldoAnterior: currency(descarga.saldoAnterior),
        bet: currency(descarga.bet),
        commission: currency(descarga.commission),
        net: currency(descarga.net),
        award: currency(descarga.award),
        liquidoTwo: currency(descarga.liquidoTwo),
        returnValue: currency(descarga.returnValue),
        lpDescarga: currency(descarga.lpDescarga),
        bancaReceived: currency(descarga.bancaReceived),
        bancaPay: currency(descarga.bancaPay),
        total: currency(descarga.total),
      };
      return this.getVisibleExportColumns().map(col => values[col.key]);
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
        } else {
          toast.error("Usuário não encontrado. Faça login novamente.");
          this.$router.push('/login');
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        if (this.isBanca) {
          // Para BANCA: buscar o BancaId do usuário e usar nova API para incluir regiões vinculadas
          const bancaResponse = await mainService.getUserBancaId(this.currentUser.id);
          this.userBancaId = bancaResponse.data;

          if (this.userBancaId) {
            // Usar nova API que busca regiões da banca incluindo vinculadas
            const bancaRegionsResponse = await mainService.getBancaRegionsWithLinked(this.userBancaId);
            this.userResponsibleRegions = bancaRegionsResponse.data.map(region => region.id);
          } else {
            this.userResponsibleRegions = [];
            toast.error("Usuário BANCA não possui BancaId definido!");
          }
        } else if (this.isAdmin) {
          // Para ADMIN: incluir todas as regiões disponíveis (filtradas por banca se selecionada)
          let allRegions = this.regions;
          
          // Se há banca selecionada, filtrar apenas regiões dessa banca
          if (this.currentSelectedBanca) {
            allRegions = allRegions.filter(region => 
              String(region.bancaId) === String(this.currentSelectedBanca.id)
            );
          }
          
          this.userResponsibleRegions = allRegions.map(region => region.id);
        } else {
          // Para GERENTE e COBRADOR: usar a API de regiões responsáveis incluindo vinculadas
          const response = await mainService.getUserRegionsWithLinked(this.currentUser.id);
          this.userResponsibleRegions = response.data.map(region => region.id);
          
          // Incluir a região principal também, se existir
          if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
            this.userResponsibleRegions.push(this.currentUser.regionId);
          }
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async searchRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
        
        // Carregar dados adicionais das regiões (incluindo nomes das regiões de descarga)
        await this.loadAdditionalRegionData();
        
        // Carregar bancas se ainda não foram carregadas (para filtro por banca)
        if (!this.bancas || this.bancas.length === 0) {
          await this.loadBancas();
        }
        
        // Aguardar carregamento das regiões do usuário para todos os tipos
        await this.loadUserResponsibleRegions();
        
        // Definir região atual se disponível
        try {
          const currentRegionStr = localStorage.getItem("currentRegion");
          const currentRegion = currentRegionStr ? JSON.parse(currentRegionStr) : null;
          if (currentRegion && currentRegion.id) {
            this.currentRegionId = currentRegion.id;
          }
        } catch (parseError) {
          // Ignorar erro de parse
        }
        
        // Filtrar apenas as regiões disponíveis para o usuário
        const regionsToSelect = this.availableRegions;
        
        if (regionsToSelect.length > 0) {
          if (this.isAdmin) {
            // ADMIN: verificar se há região selecionada
            const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
            if (currentRegion && currentRegion.id) {
              // Se há região selecionada, usar apenas ela (comparar como string para evitar problemas de tipo)
              const selectedRegion = regionsToSelect.find(r => String(r.id) === String(currentRegion.id));
              if (selectedRegion) {
                this.selectedRegions = [selectedRegion];
              } else {
                // Se a região selecionada não está disponível, usar todas
                this.selectedRegions = regionsToSelect;
              }
            } else {
              // Se não há região selecionada, usar todas
              this.selectedRegions = regionsToSelect;
            }
          } else {
            // Para outros roles, verificar se há região selecionada
            if (this.currentSelectedRegion && this.currentSelectedRegion.id) {
              // Se há região selecionada, tentar encontrar ela nas regiões disponíveis
              const selectedRegion = regionsToSelect.find(r => String(r.id) === String(this.currentSelectedRegion.id));
              if (selectedRegion) {
                // Se encontrou, usar apenas ela
                this.selectedRegions = [selectedRegion];
              } else {
                // Se não encontrou, usar todas as regiões disponíveis
                this.selectedRegions = regionsToSelect;
              }
            } else {
              // Se não há região selecionada, auto-selecionar todas as regiões disponíveis
              this.selectedRegions = regionsToSelect;
            }
          }
          
          // Verificar se as datas estão definidas antes da busca automática
          if (this.startDate && this.endDate) {
            await this.searchDescargas();
          } else {
            this.loading = false;
          }
        } else {
          this.loading = false;
          toast.warning("Nenhuma região disponível para consulta");
        }
      } catch (error) {
        this.loading = false;
        toast.error(handleErrorMessage(error));
        this.regions = [];
      }
    },

    async loadAdditionalRegionData() {
      // Buscar dados das regiões de descarga para cada região
      const promises = this.regions.map(async (region) => {
        // Se a região tem descargaRegionId, buscar o nome da região de descarga
        if (region.descargaRegionId) {
          try {
            const descargaRegion = this.regions.find(r => r.id === region.descargaRegionId);
            if (descargaRegion) {
              region.descargaRegionName = descargaRegion.name;
            } else {
              // Se não encontrou na lista atual, buscar via API
              const descargaResponse = await mainService.getRegionById(region.descargaRegionId);
              if (descargaResponse.data) {
                region.descargaRegionName = descargaResponse.data.name;
              }
            }
          } catch (error) {
            // Ignorar erro ao buscar região de descarga
          }
        }
        
        // Buscar dados da banca se existir bancaId
        if (region.bancaId) {
          try {
            const bancaInfo = this.bancas.find(b => b.id === region.bancaId);
            if (bancaInfo) {
              region.bancaName = bancaInfo.name;
            }
          } catch (error) {
            // Ignorar erro ao buscar dados da banca
          }
        }
        
        // Criar displayName para o MultiSelect
        if (region.descargaRegionName) {
          region.displayName = `${region.name} → ${region.descargaRegionName}`;
        } else {
          region.displayName = region.name;
        }
      });

      await Promise.all(promises);
    },

    async loadBancas() {
      const response = await mainService.searchBancas({ isActive: true });
      this.bancas = response.data || [];
    },

    updateSelectedRegions() {
      // Filtrar apenas as regiões disponíveis para o usuário
      const regionsToSelect = this.availableRegions;
      
      if (regionsToSelect.length > 0) {
        if (this.isAdmin) {
          // ADMIN: verificar se há região selecionada
          if (this.currentSelectedRegion && this.currentSelectedRegion.id) {
            // Se há região selecionada, tentar encontrar ela nas regiões disponíveis
            const selectedRegion = regionsToSelect.find(r => String(r.id) === String(this.currentSelectedRegion.id));
            if (selectedRegion) {
              // Se encontrou, usar apenas ela
              this.selectedRegions = [selectedRegion];
            } else {
              // Se não encontrou, usar todas
              this.selectedRegions = regionsToSelect;
            }
          } else {
            // Se não há região selecionada, usar todas
            this.selectedRegions = regionsToSelect;
          }
        } else {
          // Para outros roles, verificar se há região selecionada
          if (this.currentSelectedRegion && this.currentSelectedRegion.id) {
            // Se há região selecionada, tentar encontrar ela nas regiões disponíveis
            const selectedRegion = regionsToSelect.find(r => String(r.id) === String(this.currentSelectedRegion.id));
            if (selectedRegion) {
              // Se encontrou, usar apenas ela
              this.selectedRegions = [selectedRegion];
            } else {
              // Se não encontrou, usar todas as regiões disponíveis
              this.selectedRegions = regionsToSelect;
            }
          } else {
            // Se não há região selecionada, auto-selecionar todas as regiões disponíveis
            this.selectedRegions = regionsToSelect;
          }
        }
        
        // Limpar dados antigos e regiões de origem selecionadas
        this.descargaData = { descargas: [] };
        this.selectedOriginRegions = [];
        this.searchExecuted = false;
        
        // Buscar automaticamente se as datas estão definidas
        if (this.startDate && this.endDate) {
          this.searchDescargas();
        }
      } else {
        this.selectedRegions = [];
        this.descargaData = { descargas: [] };
        this.searchExecuted = false;
      }
    },

    async searchDescargas() {
      if (this.selectedRegions.length === 0 || !this.startDate || !this.endDate) {
        this.loading = false;
        toast.warning("Selecione pelo menos uma região e o período de busca");
        return;
      }

      this.loading = true;
      this.searchExecuted = true;
      
      try {
        const startDateTimeStr = this.startDate + "T00:00:00";
        const endDateTimeStr = this.endDate + "T23:59:59";

        // Backend espera:
        // - IdCurrentRegion: região que recebeu (destination)
        // - IdRegions: array de regiões de origem (origins) que enviaram para a destination
        // - Se IdRegions vazio ou contém todas, busca TODAS as origens que enviaram para destination
        
        // Determinar quais regiões de origem usar
        // Se há regiões de origem selecionadas, usar apenas elas
        // Caso contrário, buscar todas as regiões que enviam para as regiões de destino
        const originRegionIds = this.selectedOriginRegions && this.selectedOriginRegions.length > 0
          ? this.selectedOriginRegions.map(r => r.id || r)
          : null; // null significa buscar todas
        
        // Se apenas UMA região selecionada: buscar origens que enviaram para ela
        if (this.selectedRegions.length === 1) {
          const destinationRegion = this.selectedRegions[0];
          
          // Se há regiões de origem selecionadas, usar apenas elas
          // Caso contrário, buscar todas as regiões que enviam para esta região de destino
          let allOriginRegionIds = [];
          if (originRegionIds) {
            // Filtrar apenas as regiões de origem selecionadas que enviam para esta região de destino
            allOriginRegionIds = originRegionIds.filter(originId => {
              const originRegion = this.regions.find(r => r.id === originId);
              return originRegion && originRegion.descargaRegionId === destinationRegion.id;
            });
          } else {
            // Buscar todas as regiões que enviaram para esta região
            allOriginRegionIds = this.regions
              .filter(r => r.id !== destinationRegion.id && r.descargaRegionId === destinationRegion.id)
              .map(r => r.id);
          }
          
          const response = await mainService.searchDescargasReceived(
            destinationRegion.id, // Região que recebeu (destination)
            allOriginRegionIds.length > 0 ? allOriginRegionIds : [], // Origens que enviaram
            startDateTimeStr,
            endDateTimeStr
          );

          if (response && response.data) {
            // Backend já retorna os totais calculados automaticamente!
            this.descargaData = response.data;
          } else {
            this.descargaData = { descargas: [] };
          }
        } else {
          // Se MÚLTIPLAS regiões selecionadas: fazer uma chamada para cada região de destino
          // e acumular os resultados
          const allDescargas = [];
          let totalBet = 0;
          let totalCommission = 0;
          let totalNet = 0;
          let totalAward = 0;
          let totalLiquidoTwo = 0;
          let totalReturnValue = 0;
          let totalLpDescarga = 0;
          let totalBancaReceived = 0;
          let totalBancaPay = 0;
          let totalFinal = 0;

          for (const region of this.selectedRegions) {
            try {
              // Se há regiões de origem selecionadas, usar apenas elas
              // Caso contrário, buscar todas as regiões que enviam para esta região de destino
              let allOriginRegionIds = [];
              if (originRegionIds) {
                // Filtrar apenas as regiões de origem selecionadas que enviam para esta região de destino
                allOriginRegionIds = originRegionIds.filter(originId => {
                  const originRegion = this.regions.find(r => r.id === originId);
                  return originRegion && originRegion.descargaRegionId === region.id;
                });
              } else {
                // Buscar todas as regiões que enviaram para esta região específica
                allOriginRegionIds = this.regions
                  .filter(r => r.id !== region.id && r.descargaRegionId === region.id)
                  .map(r => r.id);
              }
              
              const response = await mainService.searchDescargasReceived(
                region.id, // Região que recebeu (destination)
                allOriginRegionIds.length > 0 ? allOriginRegionIds : [], // Origens que enviaram
                startDateTimeStr,
                endDateTimeStr
              );

              if (response && response.data) {
                const data = response.data;
                
                // Adicionar descargas ao array (já vem com dados corretos do backend)
                if (data.descargas && Array.isArray(data.descargas)) {
                  allDescargas.push(...data.descargas);
                }
                
                // Acumular totais (backend já calcula corretamente)
                totalBet += data.bet || 0;
                totalCommission += data.commission || 0;
                totalNet += data.net || 0;
                totalAward += data.award || 0;
                totalLiquidoTwo += data.liquidoTwo || 0;
                totalReturnValue += data.returnValue || 0;
                totalLpDescarga += data.lpDescarga || 0;
                totalBancaReceived += data.bancaReceived || 0;
                totalBancaPay += data.bancaPay || 0;
                totalFinal += data.total || 0;
              }
            } catch (error) {
              // Continuar com outras regiões mesmo se uma falhar
            }
          }

          // Criar objeto de dados consolidado
          this.descargaData = {
            descargas: allDescargas,
            bet: totalBet,
            commission: totalCommission,
            net: totalNet,
            award: totalAward,
            liquidoTwo: totalLiquidoTwo,
            returnValue: totalReturnValue,
            lpDescarga: totalLpDescarga,
            bancaReceived: totalBancaReceived,
            bancaPay: totalBancaPay,
            total: totalFinal
          };
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.descargaData = { descargas: [] };
      } finally {
        this.loading = false;
      }
    },

    setCurrencyString(value) {
      try {
        const numValue = Number(value) || 0;
        return jsFunctions.setCurrencyString(numValue);
      } catch (error) {
        return "R$ 0,00";
      }
    },

    calculateTotalLiquidoTwo() {
      // Backend já retorna o valor calculado no resumo consolidado
      return this.descargaData?.liquidoTwo || 0;
    },

    printReport() {
      if (!this.descargaData?.descargas || this.descargaData.descargas.length === 0) {
        toast.warning("Não há dados para imprimir");
        return;
      }

      const printWindow = window.open("", "_blank");
      const printContent = this.generatePrintHTML();
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    generatePrintHTML() {
      const showLiquido2AndRetorno = this.shouldShowLiquido2AndRetorno;
      const selectedRegionsText = this.selectedRegions.map(r => r.name || r.label).join(", ");
      const descargas = this.descargaData?.descargas || [];
      const visibleColumns = this.getVisibleExportColumns();
      
      // Resumo consolidado
      const resumoHTML = `
        <div style="margin-bottom: 30px; padding: 15px; background-color: #f5f5f5; border-radius: 8px;">
          <h2 style="margin-top: 0; color: #2c3e50;">Resumo Consolidado</h2>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
            <div>
              <strong>Total Vendas:</strong><br>
              <span style="color: #10b981; font-size: 18px;">${this.setCurrencyString(this.descargaData?.bet || 0)}</span>
            </div>
            <div>
              <strong>Total Comissões:</strong><br>
              <span style="color: #ef4444; font-size: 18px;">${this.setCurrencyString(this.descargaData?.commission || 0)}</span>
            </div>
            <div>
              <strong>Líquido Total:</strong><br>
              <span style="font-size: 18px;">${this.setCurrencyString(this.descargaData?.net || 0)}</span>
            </div>
            <div>
              <strong>Total Prêmios:</strong><br>
              <span style="color: #ef4444; font-size: 18px;">${this.setCurrencyString(this.descargaData?.award || 0)}</span>
            </div>
            ${showLiquido2AndRetorno ? `
            <div>
              <strong>Líquido 2 Total:</strong><br>
              <span style="font-size: 18px;">${this.setCurrencyString(this.descargaData?.liquidoTwo || 0)}</span>
            </div>
            ` : ""}
            <div>
              <strong>Total Final:</strong><br>
              <span style="font-size: 20px; font-weight: bold;">${this.setCurrencyString(this.descargaData?.total || 0)}</span>
            </div>
          </div>
        </div>
      `;
      
      let tableRows = "";
      descargas.forEach(descarga => {
        const cells = this.buildExportRow(descarga, true)
          .map((value, index) => {
            const isRegion = visibleColumns[index]?.key === "region";
            const isTotal = visibleColumns[index]?.key === "total";
            const style = isRegion
              ? ""
              : `text-align: right;${isTotal ? " font-weight: bold;" : ""}`;
            return `<td${style ? ` style="${style}"` : ""}>${value}</td>`;
          })
          .join("");
        tableRows += `<tr>${cells}</tr>`;
      });

      let headerRow = visibleColumns.map(col => `<th>${col.label}</th>`).join("");

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Descargas Recebidas</title>
          <style>
            @media print {
              @page { margin: 1cm; }
              body { margin: 0; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .report-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th {
              background-color: #e5e5e5;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Descargas Recebidas</h1>
          <div class="report-info">
            <p><strong>Período:</strong> ${this.startDate} até ${this.endDate}</p>
            <p><strong>Regiões:</strong> ${selectedRegionsText}</p>
            <p><strong>Data de geração:</strong> ${new Date().toLocaleString("pt-BR")}</p>
          </div>
          ${resumoHTML}
          <table>
            <thead>
              <tr>
                ${headerRow}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            <p>Total de registros: ${descargas.length}</p>
          </div>
        </body>
        </html>
      `;
    },

    exportToPDF() {
      if (!this.descargaData?.descargas || this.descargaData.descargas.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const doc = new jsPDF("landscape", "mm", "a4");
        const showLiquido2AndRetorno = this.shouldShowLiquido2AndRetorno;
        const visibleColumns = this.getVisibleExportColumns();
        
        // Título
        doc.setFontSize(16);
        doc.text("Relatório de Descargas Recebidas", 14, 15);
        
        // Informações do relatório
        doc.setFontSize(10);
        doc.text(`Período: ${this.startDate} até ${this.endDate}`, 14, 25);
        const selectedRegionsText = this.selectedRegions.map(r => r.name || r.label).join(", ");
        const regionsText = doc.splitTextToSize(`Regiões: ${selectedRegionsText}`, 260);
        doc.text(regionsText, 14, 30);
        doc.text(`Data de geração: ${new Date().toLocaleString("pt-BR")}`, 14, 35);
        
        // Resumo consolidado
        let startY = 45;
        doc.setFontSize(12);
        doc.text("Resumo Consolidado", 14, startY);
        startY += 7;
        
        doc.setFontSize(9);
        doc.text(`Total Vendas: ${this.setCurrencyString(this.descargaData?.bet || 0)}`, 14, startY);
        startY += 5;
        doc.text(`Total Comissões: ${this.setCurrencyString(this.descargaData?.commission || 0)}`, 14, startY);
        startY += 5;
        doc.text(`Líquido Total: ${this.setCurrencyString(this.descargaData?.net || 0)}`, 14, startY);
        startY += 5;
        doc.text(`Total Prêmios: ${this.setCurrencyString(this.descargaData?.award || 0)}`, 14, startY);
        startY += 5;
        if (showLiquido2AndRetorno) {
          doc.text(`Líquido 2 Total: ${this.setCurrencyString(this.descargaData?.liquidoTwo || 0)}`, 14, startY);
          startY += 5;
        }
        doc.setFontSize(10);
        doc.text(`Total Final: ${this.setCurrencyString(this.descargaData?.total || 0)}`, 14, startY);
        startY += 8;
        
        const tableColumns = visibleColumns.map(col => col.label);
        const tableData = (this.descargaData?.descargas || []).map(descarga =>
          this.buildExportRow(descarga, true),
        );
        
        // Adicionar tabela usando autoTable diretamente
        autoTable(doc, {
          head: [tableColumns],
          body: tableData,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [249, 249, 249] },
          margin: { left: 14, right: 14 },
          tableWidth: "wrap"
        });
        
        // Salvar PDF
        const fileName = `Descargas_Recebidas_${this.startDate}_${this.endDate}.pdf`;
        doc.save(fileName);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar PDF:", error);
        toast.error("Erro ao exportar PDF");
      }
    },

    exportToExcel() {
      if (!this.descargaData?.descargas || this.descargaData.descargas.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const showLiquido2AndRetorno = this.shouldShowLiquido2AndRetorno;
        const visibleColumns = this.getVisibleExportColumns();
        const headers = visibleColumns.map(col => col.label);
        const tableData = (this.descargaData?.descargas || []).map(descarga =>
          this.buildExportRow(descarga, false),
        );
        
        // Criar workbook
        const wb = XLSX.utils.book_new();
        
        // Preparar dados completos: informações + resumo + cabeçalho + dados
        const allData = [
          ["Relatório de Descargas Recebidas"],
          [""],
          ["Período:", `${this.startDate} até ${this.endDate}`],
          ["Regiões:", this.selectedRegions.map(r => r.name || r.label).join(", ")],
          ["Data de geração:", new Date().toLocaleString("pt-BR")],
          [""],
          ["Resumo Consolidado"],
          ["Total Vendas:", this.descargaData?.bet || 0],
          ["Total Comissões:", this.descargaData?.commission || 0],
          ["Líquido Total:", this.descargaData?.net || 0],
          ["Total Prêmios:", this.descargaData?.award || 0]
        ];
        
        if (showLiquido2AndRetorno) {
          allData.push(["Líquido 2 Total:", this.descargaData?.liquidoTwo || 0]);
        }
        
        allData.push(
          ["Total Final:", this.descargaData?.total || 0],
          [""],
          headers, // Cabeçalho da tabela
          ...tableData // Dados da tabela
        );
        
        // Criar worksheet com todos os dados
        const ws = XLSX.utils.aoa_to_sheet(allData);
        
        // Definir largura das colunas
        const colWidths = headers.map(() => ({ wch: 15 }));
        ws["!cols"] = colWidths;
        
        // Adicionar worksheet ao workbook
        XLSX.utils.book_append_sheet(wb, ws, "Descargas Recebidas");
        
        // Salvar arquivo
        const fileName = `Descargas_Recebidas_${this.startDate}_${this.endDate}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar Excel:", error);
        toast.error("Erro ao exportar Excel");
      }
    }
  }
};
</script>

<style scoped>
/* IMPORTANTE: Garantir que os dropdowns dos MultiSelect sobreponham todos os elementos */
/* Remover overflow: hidden dos containers para permitir que o dropdown apareça */
.entries-container .filters-card {
  overflow: visible !important;
}

.filters-content {
  overflow: visible !important;
}

.filters-grid {
  overflow: visible !important;
}

.input-group {
  overflow: visible !important;
  position: relative !important;
  z-index: 1 !important;
}

.multiselect-container {
  position: relative !important;
  z-index: 10 !important;
}

/* Quando o multiselect estiver ativo/aberto, aumentar o z-index */
.multiselect-container:has(.multiselect--active) {
  z-index: 1000 !important;
}

/* Table container com scroll horizontal */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

/* Tabela moderna com texto sem quebra de linha */
.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  white-space: nowrap;
}

.modern-table th {
  background: #e5e5e5;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
  color: #f4f4f5;
}

.modern-table td {
  padding: 0.75rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  white-space: nowrap;
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

/* Células específicas */
.region-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
  white-space: nowrap;
}

/* Valores monetários */
.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: var(--text-primary);
}

.currency-positive {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #10b981 !important;
}

.currency-negative {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #ef4444 !important;
}

.final-balance {
  font-size: 1.05rem;
  background: rgba(96, 165, 250, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

/* Resumo consolidado específico */
.summary-content {
  padding: 1.5rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.summary-item {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-family: 'Consolas', 'Monaco', monospace;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Responsividade para telas pequenas */
@media (max-width: 1024px) {
  .summary-grid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }
}

@media (max-width: 768px) {
  .modern-table {
    font-size: 0.8rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 0.5rem 0.75rem;
  }
  
  .summary-grid {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
}

@media (max-width: 480px) {
  .modern-table th,
  .modern-table td {
    padding: 0.375rem 0.5rem;
  }
  
  .summary-grid {
    grid-template-columns: 1fr;
  }
}

/* Card header com ações — sobrescreve .btn-action global (32x32 ícone) */
.results-card {
  overflow: visible !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 5;
  overflow: visible !important;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 6;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.zoom-label:hover {
  color: var(--primary-color, #3b82f6);
}

.btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
  box-sizing: border-box;
}

.btn-action i {
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.btn-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.btn-action:active {
  transform: translateY(0);
}

.btn-print {
  background-color: #6b7280;
}

.btn-print:hover {
  background-color: #4b5563;
}

.btn-pdf {
  background-color: #dc2626;
}

.btn-pdf:hover {
  background-color: #b91c1c;
}

.btn-excel {
  background-color: #16a34a;
}

.btn-excel:hover {
  background-color: #15803d;
}

.columns-dropdown {
  position: relative;
  z-index: 50;
  flex: 0 0 auto;
}

.btn-columns {
  background-color: #2563eb;
}

.btn-columns:hover {
  background-color: #1d4ed8;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #111);
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.columns-reset:hover {
  text-decoration: underline;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
  user-select: none;
}

.columns-menu-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.columns-menu-item.locked {
  opacity: 0.7;
  cursor: default;
}

.columns-menu-item input {
  width: 15px;
  height: 15px;
  accent-color: #2563eb;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .card-actions {
    width: 100%;
  }
  
  .btn-action {
    flex: 1 1 auto;
    justify-content: center;
    width: auto !important;
    height: auto !important;
  }

  .columns-dropdown {
    flex: 1 1 auto;
  }

  .columns-dropdown .btn-action {
    width: 100% !important;
  }

  .columns-menu {
    left: 0;
    right: 0;
    min-width: 0;
  }
}
</style> 